/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from 'react';
import { FieldArray, Field, Form, Formik } from 'formik';
import { useHistory } from 'react-router-dom';
import Switch from 'react-switch';
import * as yup from 'yup';
import Layout from 'components/Layout';
import ContentCard from 'components/ContentCard';
import { FitinsurAPI, MessageCenter } from 'utils';
import Colors from 'constants/Colors';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import Loader from 'components/Loader';
import Icon from 'components/Icon';
import SearchField from 'components/Form/SearchField';
import DragZoneUpload from 'components/DragZoneUpload'
import {
  contactColumns,
  relationshipColumns,
  relationshipColumnsSelect,
  employeeColumns,
  accessGroupColumns,
  insurerColumns,
  clientSupplierColumns,
  texelBranchColumns,
  surplusColumn,
  thirdPartyColumns,
  accountColumns,
  governmentStatesColumns,
  insurerColumnsRunOff
} from './columns'
import { Text } from 'components/Text';

import {
  ViewText,
  ButtonContainer,
  ButtonContent,
  Label1,
  RadioContainer,
  TextTitle,
  ContentTextarea,
  ButtonContainerRow,
  MarginDiv,
  BorderSwitch
} from './styles';

import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import Button from 'components/Button';
import ComboBox from 'components/Form/ComboBox3';
import InputField from 'components/Form/InputField';
import { RadioButton } from "components/Form/Radio/index";
import FormContainer from 'components/FormContainer';
import ContactInformation from './ContactInformation';
import AccountInformation from './AccountInformation';
import AccessGroup from './AccessGroup';
import RelationshipInformation from './RelationshipInformation';
import ClientSupplierInformation from './ClientSupplierInformation';
import EmployeeInformation from './EmployeeInformation';
import InsurerInformation from './InsurerInformation';
import TexelBranchInformation from './TexelBranchInformation';
import ThirdPartyInformation from './ThirdPartyInformation';
import SurplusInformation from './SurplusInformation';
import DialogAlertMessage from 'components/DialogAlertMessage';
import GovernmentStatesInformation from './GovernmentStatesInformation';
import ModalConfirmation from 'components/ModalConfirmation';

import Access from 'constants/Access';
import HasPermission from 'components/HasPermission/index';

export default function CreateManagement(props) {
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();
  const [searchList, setSearchList] = useState([])

  const formikRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const mandatoryCountriesIds = [17, 42, 123, 170, 194, 203, 221];


  const [validations, setValidations] = useState({});
  const [contacts, setContacts] = useState([]);
  const [affectedEnquiries, setAffectedEnquiries] = useState([]);
  const [formOptions, setFormOptions] = useState({
    countries: [],
    personTypes: [],
    contactTypes: [],
    legalIdTypes: [],
    relationshipTypes: [],
    legalEntities: [],
    contactDepartments: [],
    clients: [],
    accessGroup: [],
    products: [],
    sicCodes: [],
    states: [],
    feeTypeOptions: [],
    taxTypeOptions: [],
    rateBasisOptions: [],
    chargeTypeOptions: [],
    periodicityOptions: [],
    rating: [],
    //personStatus: []
  });
  const [idPerson, setIdPerson] = useState(props.match?.params?.idPerson);
  const [hasPermission, setHasPermission] = useState(true);
  const [hasPermissionGovernmentTaxes, setHasPermissionGovernmentTaxes] = useState(false);
  const [hasAddress, setHasAddress] = useState(true);
  const [alertDialogOpen, setAlertDialogOpen] = useState(false);

  const [accessGroupClientOptions, setAccessGroupClientOptions] = useState([]);
  const [accessGroupOptions, setAccessGroupOptions] = useState([]);
  const [idUser, setIdUser] = useState(false);
  const [showModalConfirmation, setShowModalConfirmation] = useState(false);

  const [personInsurers, setPersonInsurers] = useState([]);
  const [changeFieldState, setChangeFieldState] = useState(false);
  const [isCountrySubDivisionRequired, setIsCountrySubDivisionRequired] = useState(false);

  const [initial, setInitial] = useState({
    shortName: '',
    fullName: '',
    personType: '',
    regulatoryId: '',
    legalId: '',
    legalIdType: {
      idDomain: '',
      domainDescription: '',
      idDomainType: '',
    },
    contactType: '',
    description: '',
    street: '',
    number: '',
    complement: '',
    zipCode: '',
    district: '',
    city: '',
    state: '',
    country: '',
    countrySubdivision: '',
    sicCode: '',
    rating: '',
    //personStatus: ''
  });

  const [personRelationships, setPersonRelationships] = useState({
    approvedMarkets: [],
    clientSuppliers: [],
    employers: [],
    insurers: [],
    branches: [],
    surplusBrokers: [],
    thirdPartyBrokers: [],
    banks: [],
    governmentTaxes: []
  })

  const [accessGroups, setAccessGroups] = useState([]);

  async function permissionAccess() {
    try {
      const response = await fitinsur
        .requestAccess("", idPerson ? "UPDATE" : "CREATE", Access.PERSON_EDIT, null, props?.idOperation, authContext.token)
        .then((response) => response.json())
      const hasPermission = response && response.ret_code === 200;
      setHasPermission(hasPermission);
    } catch (e) {
      setHasPermission(false);
    }
  }

  async function permissionAccessGovernmentTaxes() {
    if (formikRef?.current?.values?.personType.idDomain && formikRef?.current?.values?.personType?.idDomain !== 615) {
      setHasPermissionGovernmentTaxes(true);
      return;
    }

    try {
      const response = await fitinsur
        .requestAccess("", idPerson ? "UPDATE" : "CREATE", Access.PERSON_EDIT_GOVERNMENT, null, props?.idOperation, authContext.token)
        .then((response) => response.json())
      const hasPermission = response && response.ret_code === 200;
      setHasPermissionGovernmentTaxes(hasPermission);
    } catch (e) {
      setHasPermissionGovernmentTaxes(false);
    }
  }

  const searchCompanies = async (searchValue, values, searchHolding) => {
    const searchValueURI = encodeURIComponent(searchValue);
    let personType, hierarchy;

    if (values?.personType.idDomain === 615) {
      personType = 615;
      hierarchy = 'government';
    } else {
      personType = 35;
      hierarchy = 'holding';
    }

    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestGetPersonsList(`?fullName=${searchValueURI}${values?.hierarchy === 'Legal Entity' || searchHolding ? `&hierarchy=${hierarchy}` : ""}&personType=${personType}`).then((response) => response.json()).then((data) => {
        if (data.ret_code != 200) {
          return;
        }

        data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.lastName}` }))
        setSearchList(data.ret_value);
      });
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const searchCountrySubDivision = async (countryId, subdivisionName) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      const data = await fitinsur.requestPersonList("fitGetCountrySubDivisions", {
        id: "fitGetCountrySubDivisions",
        countryId,
        subdivisionName
      }).then((response) => response.json())

      if (data.ret_code != 200) {
        return;
      }

      data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.ID_COUNTRY_SUBDIVISION, value: `${v.SUBDIVISION_NAME}`, personName: `${v.SUBDIVISION_NAME}` }))
      setSearchList(data.ret_value);
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }

  const searchLegalEntities = async (searchValue, values) => {
    try {
      if (values?.parentCompanyId) {
        fitinsur.setAuthToken(authContext.token);
        fitinsur.requestGetPersonsList(`?fullName=${searchValue}&personType=35&idPersonRoot=${values?.parentCompanyId}`).then((response) => response.json()).then((data) => {
          if (data.ret_code != 200) {
            return;
          }

          data.ret_value = data.ret_value?.map(v => ({ ...v, id: v.idPerson, personName: `${v.lastName}` }))
          setSearchList(data.ret_value);
        });
      }
    } catch (err) {
      console.log("FETCH_FAILURE: ", err);
    }
  }


  const getSectionName = (answerOptions, idDomain) => {
    const option = answerOptions.find(v => v.idDomain === idDomain);
    if (option) return option.description
  }

  const getFiledOptions = function (answerOptions) {
    if (!hasPermissionGovernmentTaxes) answerOptions = answerOptions.filter(answerOption => answerOption.idDomain !== 615);

    return answerOptions.map(option => {
      if (option.countryId) {
        return {
          label: option.countryName,
          value: {
            countryId: option.countryId,
            countryName: option.countryName,
            value: option.countryId,
            label: option.countryName,
          }
        };
      } else if (option.idDomain) {
        return {
          label: option.description,
          value: {
            idDomain: option.idDomain,
            description: option.description,
            idDomainType: option.idDomainType,
          }
        };
      } else if (option.idQuestion === 68) {
        return {
          label: option.dsAnswerOption,
          value: {
            currencyId: option.idAnswerOption,
            currencyName: option.dsAnswerOption,
          }
        }
      } else if (option.idSicCode) {
        if (option.sicType !== 'TEXEL_UK') return null;
        return {
          label: option.sicDescription,
          value: option.sicDescription
        }
      } else if (option.idRating) {
        return {
          label: option.description,
          value: { idRating: option.idRating, value: option.description, description: option.description }
        }
      }
      else if (option.dsAnswerOption) {
        return {
          label: option.dsAnswerOption,
          value: {
            value: option.idAnswerOption,
            label: option.dsAnswerOption,
            stateId: option.idAnswerOption,
            stateName: option.dsAnswerOption,
          }
        }

      }
    }).filter(v => v);;
  }

  const getPersonInsurer = async () => {
    try {
      if (!idPerson) return getFormOptions();

      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetPersonsInsurer().then(res => res.json())
      if (response.ret_code !== 200) mc.error(response.ret_msg_details);

      loading.stop();
      let {
        firstName,
        lastName,
        legalPersonId,
        susepNumber,
        personType,
        legalIdType,
        hierarchy,
        formOptions,
        contacts,
        parentCompany,
        personRelationships,
        legalEntity,
        hasAddress,
        idUser,
        accessGroups,
        sicCode,
        idDomainStatus,
        personStatus
      } = response.ret_value;

      setPersonInsurers(response.ret_value)

    } catch (error) {
      console.log('ERROR', error);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }
  const getPerson = async () => {
    try {
      if (!idPerson) return getFormOptions();

      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetPersonsInfo(idPerson).then(res => res.json())
      if (response.ret_code !== 200) mc.error(response.ret_msg_details);

      loading.stop();
      let {
        firstName,
        lastName,
        legalPersonId,
        susepNumber,
        personType,
        legalIdType,
        hierarchy,
        formOptions,
        contacts,
        parentCompany,
        personRelationships,
        legalEntity,
        hasAddress,
        idUser,
        accessGroups,
        sicCode,
        rating,
        idDomainStatus,
        personStatus
      } = response.ret_value;

      formikRef.current.values.shortName = firstName || "";
      formikRef.current.values.fullName = lastName || "";
      formikRef.current.values.legalPersonId = legalPersonId || "";
      formikRef.current.values.regulatoryId = susepNumber || 'N/A';
      formikRef.current.values.personType = personType || "";
      formikRef.current.values.legalIdType = legalIdType || "";
      formikRef.current.values.rating = rating || "";
      formikRef.current.values.sicCode = sicCode?.sicDescription || "";
      formikRef.current.values.hierarchy = hierarchy;
      formikRef.current.values.parentCompany = parentCompany?.lastName;
      formikRef.current.values.parentCompanyId = parentCompany?.idPerson;
      formikRef.current.values.legalEntity = legalEntity?.lastName;
      formikRef.current.values.legalEntityId = legalEntity?.idPerson;
      //formikRef.current.values.personStatus = personStatus || ""

      setFormOptions(formOptions);
      setContacts(contacts);
      setHasAddress(hasAddress);
      setIdUser(idUser);
      setAccessGroups(accessGroups);

      const relationships = {
        approvedMarkets: personRelationships[314] || [],
        clientSuppliers: personRelationships[290] || [],
        employers: personRelationships[291] || [],
        insurers: personRelationships[365] || [],

        branches: personRelationships[379] || [],

        surplusBrokers: personRelationships[448] || [],
        thirdPartyBrokers: personRelationships[449] || [],
        banks: personRelationships[380] || [],
        governmentTaxes: personRelationships[616] || [],
        runOff: personRelationships[628] || []
      }

      relationships.insurers = relationships.insurers.map((insurer) => {
        const statusRunOff = relationships?.runOff?.find(statusRunOff => insurer?.idPersonTail == statusRunOff?.idPersonTail);

        const newInsurer = { ...insurer };
        newInsurer.relationshipContent.runOff = statusRunOff ? true : false;

        if (statusRunOff) {
          newInsurer.idPersonRelationshipRunOff = statusRunOff?.idPersonRelationship;
        }
        return newInsurer;
      });

      setPersonRelationships(relationships)
    } catch (error) {
      console.log('ERROR', error);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const getFormOptions = async () => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetPersonFormOptions()
        .then(res => res.json());

      if (response.ret_code != 200) {
        loading.stop();

        mc.error(response.ret_msg_details);
      }

      loading.stop();
      setFormOptions(response.ret_value);
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const savePerson = async (body) => {
    body = JSON.parse(JSON.stringify(body));

    try {
      setAlertDialogOpen(false);
      if (idPerson) body.idPerson = idPerson;

      loading.start();
      fitinsur.setAuthToken(authContext.token);
      if (body?.state?.stateName) {
        body.state = body.state.stateName;
      }

      if (body.countrySubdivision?.id) {
        body.countrySubdivision = body.countrySubdivision?.id;
      }

      const response = await fitinsur.requestSavePersonInfo(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      mc.success("Registration successful!");
      loading.stop();

      if (!idPerson) {
        history.push(`/management/person/${response.ret_value}`);
        window.location.reload();
      }
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const getAffectedEnquiries = (values) => {
    if (!idPerson) return savePerson(values);

    if (idPerson) {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestListEnquiries(idPerson).then(x => x.json())
        .then(resp => {
          loading.stop();
          if (resp.ret_code === 200) {
            if (!resp.ret_value.length) return savePerson(values);
            setAffectedEnquiries(resp.ret_value);
            setAlertDialogOpen(true);
          }
        }).catch(() => {
          loading.stop();
          mc.error("There was an error while fetching your data");
        });
    }
  }

  const createValidation = () => {
    const existsCountry = contacts?.find((contact) => contact.idDomain === 30);
    let validations = yup.object().shape({
      hierarchy: yup.string().required("* Required field"),
      shortName: yup.string().required("* Required field"),
      fullName: yup.string().required("* Required field"),
      personType: yup.string().required("* Required field"),
      sicCode: formikRef.current.values.personType?.idDomain === 35 ? yup.string().required("* Required field") : yup.string().notRequired(),
      regulatoryId: yup.string().notRequired(),
      legalPersonId: yup.string().notRequired(),
      legalIdType: yup.string().notRequired(),
      street: yup.string().notRequired(),
      number: yup.string().notRequired(),
      complement: yup.string().notRequired(),
      zipCode: yup.string().when('hierarchy', (hierarchy) => idPerson || hierarchy !== 'Legal Entity' ? yup.string().notRequired() : yup.string().required("* Required field")),
      district: yup.string().notRequired(),
      city: yup.string().notRequired(),
      state: yup.string().when('hierarchy', (hierarchy) => formikRef.current.values.personType?.idDomain === 615 && hierarchy === 'Legal Entity' ? yup.string().required("* Required field") : yup.string().notRequired()),
      country: yup.string().when('hierarchy', (hierarchy) => hierarchy === 'Individual' ? yup.string().notRequired() : existsCountry ? yup.string().notRequired() : yup.string().required("* Required field")),
      countrySubdivision: isCountrySubDivisionRequired ? yup.string().required("* Required field") : yup.string().notRequired(),
      parentCompany: yup.string().when('hierarchy', (hierarchy) => hierarchy === 'Holding' ? yup.string().notRequired() : yup.string().required("* Required field")),
      legalEntity: yup.string().when('hierarchy', (hierarchy) => hierarchy !== 'Individual' ? yup.string().notRequired() : yup.string().required("* Required field")),
      email: yup.string().email().notRequired(),
      //personStatus: yup.string().notRequired()
    });
    setValidations(validations);
    return validations
  }

  const loadContent = async () => {
    setIdPerson(props.match?.params?.idPerson)
    await createValidation();
    await getPerson();
    await getPersonInsurer();
    await permissionAccess();
    await permissionAccessGovernmentTaxes();
    await handleGetAccessGroupClients();
    loading.stop();
  }

  const handleGetAccessGroupClients = async () => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetAccessGroupClients()
        .then(res => res.json());

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setAccessGroupClientOptions(response.ret_value);

    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const handleGetAccessGroupByRelationship = async (idPersonRelationship) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetAccessGroupByRelationship(idPersonRelationship)
        .then(res => res.json());

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setAccessGroupOptions(response.ret_value);
      loading.stop();

    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  useEffect(() => {
    loadContent();
  }, [initial]);

  useEffect(() => {
    setIdPerson(props.match?.params?.idPerson);
    loadContent();
  }, [props.match.params?.idPerson])


  function renderModalApproval(values) {
    return (
      <DialogAlertMessage icon='exclamation' title="Attention" size='16px' show={alertDialogOpen} setDialog={() => setAlertDialogOpen(false)}>
        <ContentTextarea>
          <Text>
            You are about to make changes into a party that is being used in the below deals.
            Please note that, after these changes are saved, all these deals will need to be manually re-processed to ensure consistency:
          </Text>
          <div style={{ maxHeight: 250, overflow: 'auto' }}>
            {affectedEnquiries.map((enquiry, index) => index + 1 <= 7 ? <p>{enquiry}</p> : <></>)}
          </div>
          <ButtonContainerRow>
            <ButtonContent><Button secundary onClick={() => setAlertDialogOpen(false)}>CANCEL</Button></ButtonContent>
            <ButtonContent><Button style={{ backgroundColor: Colors.BLUE, color: Colors.WHITE, marginLeft: 15 }} onClick={() => savePerson(values)}>CONFIRM</Button></ButtonContent>
          </ButtonContainerRow>
        </ContentTextarea>
      </DialogAlertMessage>
    )
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => getAffectedEnquiries(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <Layout idOperation={props?.idOperation} title={values.fullName || "New Register"}>
          {renderModalApproval(values)}
          <ContentCard title="PARTY INFORMATION">
            <ViewText>
              <TextTitle>*Required Fields</TextTitle>
            </ViewText>

            <Form>
              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="shortName"
                    label="Short Name"
                    isRequired
                    component={InputField}
                    value={values.shortName}
                    disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="fullName"
                    label="Full Name"
                    isRequired
                    component={InputField}
                    value={values.fullName}
                    disabled={!hasPermission || (!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="legalIdType"
                    label="Legal ID Document Type"
                    onChange={(e) => { setFieldValue(`legalIdType`, e); }}
                    options={getFiledOptions(formOptions.legalIdTypes)}
                    searchList={formOptions.legalIdTypes}
                    component={ComboBox}
                    value={values.legalIdType}
                    disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="legalPersonId"
                    label="Legal ID"
                    component={InputField}
                    value={values.legalPersonId}
                    disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                  />
                </Col>
              </Row>

              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="personType"
                    label="Person Type"
                    options={getFiledOptions(formOptions.personTypes)}
                    searchList={formOptions.personTypes}
                    onChange={(e) => {
                      setFieldValue('hierarchy', '');
                      setFieldValue('personType', e)
                    }}
                    component={ComboBox}
                    isRequired
                    value={values.personType}
                    disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                  />
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="regulatoryId"
                    label="Regulatory ID"
                    onChange={(e) => { setFieldValue(`regulatoryId`, e); }}
                    component={InputField}
                    value={values.regulatoryId}
                    disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                  />
                </Col>
              </Row>


              <Row>
                {values.personType?.idDomain !== 615 && (
                  <Col lg={6} md={6} sm={6} xs={12}>
                    <Field
                      name="sicCode"
                      label="SIC Code"
                      options={getFiledOptions(formOptions.sicCodes)}
                      searchList={formOptions.sicCodes}
                      onChange={(e) => {
                        setFieldValue('sicCode', e)
                      }}
                      component={ComboBox}
                      isRequired={values.personType?.idDomain === 35}
                      value={values.sicCode.sicDescription}
                      disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                    />
                  </Col>
                )}
                {values.personType?.idDomain === 35 &&
                  <Col lg={6} md={6} sm={6} xs={12}>
                    <Field
                      name="rating"
                      label="Rating"
                      options={getFiledOptions(formOptions.rating)}
                      searchList={formOptions.rating}
                      onChange={(e) => {
                        setFieldValue('rating', e)
                      }}
                      component={ComboBox}
                      value={values.rating?.description}
                    />
                  </Col>
                }
              </Row>

              <Row>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Label1>* Hierarchy</Label1>
                  <Row>
                    <RadioContainer>
                      {values.personType?.idDomain === 34 && <Field
                        label={'Individual'}
                        name={'hierarchy'}
                        value={values.hierarchy}
                        onChange={(e) => { setFieldValue('hierarchy', e) }}
                        option='Individual'
                        component={RadioButton}
                        disabled={values.personType?.idDomain !== 34 || (!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        checked={values.hierarchy === 'Individual'}
                      />}

                      {(values.personType?.idDomain === 35 || values.personType?.idDomain === 615) &&
                        <>
                          <Field
                            style={{ marginRight: 40 }}
                            label={'Legal Entity'}
                            name={'hierarchy'}
                            value={values.hierarchy}
                            onChange={(e) => setFieldValue(`hierarchy`, e)}
                            option='Legal Entity'
                            component={RadioButton}
                            disabled={(values.personType?.idDomain !== 35 && values.personType?.idDomain !== 615) || (!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                            checked={values.hierarchy === 'Legal Entity'}
                          />
                          <MarginDiv>
                            <Field
                              label={'Holding'}
                              name={'hierarchy'}
                              value={values.hierarchy}
                              onChange={(e) => {
                                setFieldValue('hierarchy', e)
                                setFieldValue(`parentCompany`, '');
                                setFieldValue(`parentCompanyId`, '');
                              }}
                              option='Holding'
                              component={RadioButton}
                              disabled={(values.personType?.idDomain !== 35 && values.personType?.idDomain !== 615) || (!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                              checked={values.hierarchy === 'Holding'}
                            />
                          </MarginDiv>
                        </>}
                    </RadioContainer>
                  </Row>
                </Col>
                <Col lg={6} md={6} sm={6} xs={12}>
                  <Field
                    name="parentCompany"
                    label="Holding"
                    component={SearchField}
                    isRequired={values.hierarchy !== 'Holding'}
                    disabled={(!values.hierarchy || values.hierarchy === 'Holding' || values.hierarchy === "Government") || (!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                    searchList={searchList}
                    value={values.parentCompany && values.parentCompany}
                    onChange={(value) => {
                      searchCompanies(value, values, true);
                    }}
                    onFocus={() => {
                      setSearchList([]);
                      searchCompanies(values && values.parentCompany || "", values, true);
                    }}
                    canGenerate={false}
                    onSearchSelect={(selectedValue) => {
                      setFieldValue(`parentCompany`, selectedValue.lastName);
                      setFieldValue(`parentCompanyId`, selectedValue.idPerson);
                      setSearchList([]);
                    }}
                  />
                </Col>
              </Row>
              {(values.personType?.idDomain !== 35 && values.personType?.idDomain !== 615) &&
                <Row>
                  <Col lg={6} md={6} sm={6} xs={12}>
                    <Field
                      name="legalEntity"
                      label="Legal Entity"
                      component={SearchField}
                      isRequired={values.hierarchy !== 'Holding'}
                      searchList={searchList}
                      value={values.legalEntity && values.legalEntity}
                      onChange={(value) => { searchLegalEntities(value, values); }}
                      onFocus={() => {
                        setSearchList([]);
                        searchLegalEntities(values && values.legalEntity || "", values);
                      }}
                      canGenerate={false}
                      onSearchSelect={(selectedValue) => {
                        setFieldValue(`legalEntity`, selectedValue.lastName);
                        setFieldValue(`legalEntityId`, selectedValue.idPerson);
                        setSearchList([]);
                      }}
                      disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                    />
                  </Col>
                </Row>
              }

              {/* {values.personType?.idDomain === 35 && (
                  <Row>
                  <Col lg={6} md={6} sm={6} xs={12}>
                    <Field
                      name="personStatus"
                      label="Person Status"
                      options={getFiledOptions(formOptions.personStatus)}
                      searchList={formOptions.personStatus}
                      onChange={(e) => {
                        setFieldValue('personStatus', e)
                      }}
                      component={ComboBox}
                      value={values.personStatus}
                    />
                  </Col>
                </Row>
              )} */}


              {!idPerson &&
                <>
                  {values.personType?.idDomain !== 34 && <>
                    <Row style={{ margin: '20px 0 10px 0' }}>
                      <Col>
                        <ViewText style={{ justifyContent: 'flex-start' }}>
                          <TextTitle size={14}>CONTACT INFORMATION</TextTitle>
                        </ViewText>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={12} sm={8} md={8} lg={8} xl={8}>
                        <Field
                          name="street"
                          label="Street"
                          component={InputField}
                          disabled={!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615}
                        />
                      </Col>
                      <Col lg={4} md={4} sm={4} xs={12}>
                        <Field
                          name="doorNumber"
                          label="Number"
                          component={InputField}
                          disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={4} md={4} sm={4} xs={12}>
                        <Field
                          name="complement"
                          label="Complement"
                          component={InputField}
                          disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        />
                      </Col>
                      <Col lg={4} md={4} sm={4} xs={12}>
                        <Field
                          name="zipCode"
                          label="Zip Code"
                          component={InputField}
                          isRequired={values.hierarchy === 'Legal Entity'}
                          disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        />
                      </Col>
                      <Col lg={4} md={4} sm={4} xs={12}>
                        <Field
                          name="district"
                          label="District"
                          component={InputField}
                          disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={4} md={4} sm={4} xs={12}>
                        <Field
                          name="city"
                          label="City"
                          component={InputField}
                          disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        />
                      </Col>
                      {
                        changeFieldState ?
                          <Col lg={4} md={4} sm={4} xs={12}>
                            <Field
                              name="state"
                              label="State"
                              component={ComboBox}
                              isRequired={values.hierarchy === 'Legal Entity' && values.personType?.idDomain === 615}
                              disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                              options={getFiledOptions(formOptions.states) || []}
                              searchList={getFiledOptions(formOptions.states) || []}
                              onChange={(e) => { setFieldValue(`state`, e); }}
                            />
                          </Col>
                          : <Col lg={4} md={4} sm={4} xs={12}>
                            <Field
                              name="state"
                              label="State"
                              component={InputField}
                              isRequired={values.hierarchy === 'Legal Entity' && values.personType?.idDomain === 615}
                              disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                            />
                          </Col>
                      }
                      <Col lg={4} md={4} sm={4} xs={12}>
                        <Field
                          name="country"
                          label="Country"
                          isRequired
                          options={getFiledOptions(formOptions.countries)}
                          searchList={formOptions.countries}
                          onChange={(e) => {
                            setFieldValue(`country`, e);
                            if (e.countryId === 221) {
                              setChangeFieldState(true)
                            } else {
                              setChangeFieldState(false)
                            }
                            setFieldValue(`state`, '')
                            setFieldValue(`countrySubdivision`, '')

                            if (mandatoryCountriesIds.includes(e.countryId)) {
                              setIsCountrySubDivisionRequired(true)
                            } else {
                              setIsCountrySubDivisionRequired(false)
                            }
                            setFieldValue(`country`, e);
                          }}
                          component={ComboBox}
                          disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col lg={4} md={4} sm={4} xs={12}>
                        <Field
                          name="countrySubdivision"
                          label="Country Sub-Division"
                          component={SearchField}
                          isRequired={isCountrySubDivisionRequired}
                          disabled={(!values.country?.countryId || (!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615))}
                          searchList={searchList}
                          value={values.countrySubdivision?.value || values.countrySubdivision || ''}
                          onChange={(value) => {
                            searchCountrySubDivision(values.country?.countryId, value);
                          }}
                          onFocus={() => {
                            setSearchList([]);
                            searchCountrySubDivision(values.country?.countryId);
                          }}
                          canGenerate={false}
                          onSearchSelect={(selectedValue) => {
                            setFieldValue(`countrySubdivision`, selectedValue);
                            setSearchList([]);
                          }}

                        />
                      </Col>
                    </Row>
                  </>}
                  <Row>
                    <Col xs={12} sm={6} md={6} lg={6} xl={6}>
                      <Field
                        label="E-mail"
                        name="email"
                        component={InputField}
                        disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                      />
                    </Col>
                    {values.personType?.idDomain !== 34 &&
                      <Col lg={6} md={6} sm={6} xs={12}>
                        <Field
                          label="Telephone"
                          name="phone"
                          component={InputField}
                          disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                        />
                      </Col>}
                  </Row>
                </>
              }


              {idPerson && <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }}>
                  <ButtonContent>
                  </ButtonContent>
                </ButtonContainer>
                <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15, marginBottom: 40 }}
                    type="submit"
                    primary
                    disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    SAVE
                  </Button>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15, marginBottom: 40 }}
                    type="button"
                    primary
                    disabled={(!hasPermissionGovernmentTaxes && values.personType?.idDomain === 615)}
                    onClick={() => setShowModalConfirmation(true)}
                  >
                    CREATE NEW PARTY
                  </Button>
                </ButtonContent>
              </ButtonContainer>}

              {idPerson && showModalConfirmation ? <ModalConfirmation
                text="Are you sure you want to proceed? Your changes could not be saved."
                showTitle={false}
                show={true}
                onConfirm={() => {
                  setShowModalConfirmation(false);
                  history.push('/management/person');
                  window.location.reload();
                }}
                onCancel={() => {
                  setShowModalConfirmation(false)
                }}
              /> : null}

              {idPerson &&
                <>
                  {hasPermissionGovernmentTaxes && (
                    <Row>
                      <Col lg={12} md={12} sm={12} xs={12}>
                        <FormContainer show={true} title="Contact Information">
                          <ContactInformation
                            setSearchList={setSearchList}
                            searchList={searchList}
                            searchCountrySubDivision={searchCountrySubDivision}
                            columns={contactColumns}
                            data={contacts}
                            contactTypes={getFiledOptions(formOptions.contactTypes)}
                            countries={getFiledOptions(formOptions.countries)}
                            states={getFiledOptions(formOptions.states)}
                            hasPermission={hasPermission}
                            idPerson={idPerson}
                            onSaveContact={async () => await getPerson()}
                            isHolding={values.hierarchy === 'Holding'}
                          />
                        </FormContainer>
                      </Col>
                    </Row>
                  )}

                  {(values.personType?.idDomain === 615 && values.hierarchy === 'Legal Entity') && (
                    <Row>
                      <Col lg={12} md={12} sm={12} xs={12}>
                        <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 615) || "Government-Taxes"}`}>
                          <GovernmentStatesInformation
                            columns={governmentStatesColumns}
                            data={personRelationships.governmentTaxes}
                            hasPermission={hasPermission}
                            idPerson={idPerson}
                            onSavePersonRelationship={async () => await getPerson()}
                            feeTypeOptions={getFiledOptions(formOptions.feeTypeOptions)}
                            taxTypeOptions={getFiledOptions(formOptions.taxTypeOptions)}
                            rateBasisOptions={getFiledOptions(formOptions.rateBasisOptions)}
                            chargeTypeOptions={getFiledOptions(formOptions.chargeTypeOptions)}
                            periodicityOptions={getFiledOptions(formOptions.periodicityOptions)}
                          />
                        </FormContainer>
                      </Col>
                    </Row>
                  )}

                  {hasPermissionGovernmentTaxes && (
                    <HasPermission permission={idUser}>
                      <Row>
                        <Col lg={12} md={12} sm={12} xs={12}>
                          <FormContainer show={true} title="Access Group">
                            <AccessGroup
                              columns={accessGroupColumns}
                              data={accessGroups}
                              options={{ clients: accessGroupClientOptions, accessGroup: accessGroupOptions }}
                              idUser={idUser}
                              onSelectClient={idPersonRelationship => handleGetAccessGroupByRelationship(idPersonRelationship)}
                              onSaveGroupMembership={async () => await getPerson()}
                            />
                          </FormContainer>
                        </Col>
                      </Row>
                    </HasPermission>)}

                  {(hasAddress || values.personType?.idDomain === 34) && <>
                    <Row style={{ margin: '20px 0 10px 0' }}>
                      <Col>
                        <ViewText style={{ justifyContent: 'flex-start' }}>
                          <TextTitle size={14}>RELATIONSHIP INFORMATION</TextTitle>
                        </ViewText>
                      </Col>
                    </Row>

                    {hasPermissionGovernmentTaxes && <>
                      <Row>
                        <Col lg={12} md={12} sm={12} xs={12}>
                          <FormContainer show={true} title={"Account Information"}>
                            <AccountInformation
                              columns={accountColumns}
                              data={personRelationships.banks}
                              products={formOptions.products}
                              hasPermission={hasPermission}
                              idPerson={idPerson}
                              onSavePersonRelationship={async () => await getPerson()}
                            />
                          </FormContainer>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={12} md={12} sm={12} xs={12}>
                          <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 314) || "Approved Market"}`}>
                            <RelationshipInformation
                              columns={relationshipColumns}
                              columnsSelect={relationshipColumnsSelect}
                              data={personRelationships.approvedMarkets}
                              personInsurers={personInsurers}
                              hasPermission={hasPermission}
                              idPerson={idPerson}
                              onSavePersonRelationship={async () => await getPerson()}
                            />
                          </FormContainer>
                        </Col>
                      </Row>

                      <Row>
                        <Col lg={12} md={12} sm={12} xs={12}>
                          <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 290) || "Client Supplier"}`}>
                            <ClientSupplierInformation
                              columns={clientSupplierColumns}
                              data={personRelationships.clientSuppliers}
                              hasPermission={hasPermission}
                              idPerson={idPerson}
                              onSavePersonRelationship={async () => await getPerson()}
                            />
                          </FormContainer>
                        </Col>
                      </Row>
                    </>}

                    {hasPermissionGovernmentTaxes && values.personType?.idDomain === 34 && values.legalEntityId &&
                      <Row>
                        <Col lg={12} md={12} sm={12} xs={12}>
                          <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 291) || "Employee"}`}>
                            <EmployeeInformation
                              columns={employeeColumns}
                              data={personRelationships.employers}
                              options={{ contactDepartments: formOptions.contactDepartments }}
                              hasPermission={hasPermission}
                              idPerson={idPerson}
                              legalEntity={values.legalEntityId}
                              onSavePersonRelationship={async () => await getPerson()}
                            />
                          </FormContainer>
                        </Col>
                      </Row>}

                    {hasPermissionGovernmentTaxes && values.hierarchy === 'Holding' && <Row>
                      <Col lg={12} md={12} sm={12} xs={12}>
                        <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 365) || "Insurer"}`}>
                          <InsurerInformation
                            columns={insurerColumns}
                            columnsRunOff={insurerColumnsRunOff}
                            data={personRelationships.insurers}
                            dataRunOff={personRelationships.insurers}
                            hasPermission={hasPermission}
                            idPerson={idPerson}
                            onSavePersonRelationship={async () => await getPerson()}
                          />
                        </FormContainer>
                      </Col>
                    </Row>}

                    {hasPermissionGovernmentTaxes && values.hierarchy === 'Holding' && <Row>
                      <Col lg={12} md={12} sm={12} xs={12}>
                        <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 379) || "Branch"}`}>
                          <TexelBranchInformation
                            columns={texelBranchColumns}
                            data={personRelationships.branches}
                            hasPermission={hasPermission}
                            idPerson={idPerson}
                            onSavePersonRelationship={async () => await getPerson()}
                          />
                        </FormContainer>
                      </Col>
                    </Row>}

                    {hasPermissionGovernmentTaxes && values.hierarchy === 'Holding' && <Row>
                      <Col lg={12} md={12} sm={12} xs={12}>
                        <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 448) || "Surplus"}`}>
                          <SurplusInformation
                            columns={surplusColumn}
                            data={personRelationships.surplusBrokers}
                            hasPermission={hasPermission}
                            idPerson={idPerson}
                            onSavePersonRelationship={async () => await getPerson()}
                          />
                        </FormContainer>
                      </Col>
                    </Row>}


                    {hasPermissionGovernmentTaxes && values.hierarchy === 'Holding' && <Row>
                      <Col lg={12} md={12} sm={12} xs={12}>
                        <FormContainer show={true} collapsedAtStart title={`${getSectionName(formOptions.relationshipTypes, 449) || "Third Party"}`}>
                          <ThirdPartyInformation
                            columns={thirdPartyColumns}
                            data={personRelationships.thirdPartyBrokers}
                            hasPermission={hasPermission}
                            idPerson={idPerson}
                            onSavePersonRelationship={async () => await getPerson()}
                          />
                        </FormContainer>
                      </Col>
                    </Row>}
                  </>}
                </>
              }

              <ButtonContainer>
                <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }}>
                  <ButtonContent>
                    <Button forth onClick={() => history.goBack()} style={{ alignSelf: 'flex-start' }}> &lt; Back</Button>
                  </ButtonContent>
                </ButtonContainer>
                {!idPerson && <ButtonContent>
                  <Button
                    fullWidth
                    style={{ marginLeft: 15 }}
                    type="submit"
                    primary
                    onClick={() => {
                      createValidation().validate(values).catch((e) => {
                        console.log('validation error', e)
                        mc.error('Please fill in all required fields.')
                      })
                    }}
                    id="btnSubmit">
                    ADD PARTY
                  </Button>
                </ButtonContent>}
              </ButtonContainer>
            </Form>

            {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
          </ContentCard>
        </Layout >
      )}
    </Formik>
  )
}